import * as React from "react";
import Box from "@mui/material/Box";

/**
 * The design system's three button shapes, defined once.
 *
 * They were previously hand-rolled at each call site, which is how the two
 * user dialogs drifted apart. Values come from NeuraDesigns
 * `userport/index.html` (`.btn.primary`, `.btn-danger`, `.btn-ghost`).
 *
 * Note `.btn-danger` is one of three class names the design system defines
 * twice: `neura.css` draws it as a solid red fill, the userport page as this
 * outlined pill. The page is newer, so the pill wins.
 */

const base = {
  fontFamily: "inherit",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  whiteSpace: "nowrap",
  transition: "all 0.16s",
  "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
  "& svg": { width: 14, height: 14, flexShrink: 0, display: "block" },
};

export function PrimaryButton({ children, sx, ...props }) {
  return (
    <Box
      component="button"
      sx={{
        ...base,
        padding: "9px 16px",
        borderRadius: "999px",
        fontSize: "0.84rem",
        fontWeight: 600,
        border: "none",
        background: "linear-gradient(135deg, var(--accent) 0%, var(--accent-2) 100%)",
        color: "var(--bg)",
        "&:hover:not(:disabled)": {
          transform: "translateY(-1px)",
          boxShadow: "0 6px 18px rgba(253, 185, 64, 0.22)",
        },
        ...sx,
      }}
      {...props}
    >
      {children}
    </Box>
  );
}

export function DangerButton({ children, sx, ...props }) {
  return (
    <Box
      component="button"
      sx={{
        ...base,
        padding: "9px 16px",
        borderRadius: "999px",
        fontSize: "0.84rem",
        fontWeight: 600,
        background: "transparent",
        border: "1px solid rgba(239, 68, 68, 0.4)",
        color: "#FCA5A5",
        "&:hover:not(:disabled)": {
          background: "rgba(239, 68, 68, 0.1)",
          borderColor: "rgba(239, 68, 68, 0.7)",
          color: "#FEE2E2",
        },
        ...sx,
      }}
      {...props}
    >
      {children}
    </Box>
  );
}

export function GhostButton({ children, sx, ...props }) {
  return (
    <Box
      component="button"
      sx={{
        ...base,
        padding: "8px 14px",
        borderRadius: "8px",
        fontSize: "0.82rem",
        fontWeight: 600,
        background: "transparent",
        color: "rgba(255, 255, 255, 0.7)",
        border: "1px solid rgba(255, 255, 255, 0.12)",
        "&:hover:not(:disabled)": { background: "rgba(255, 255, 255, 0.04)", color: "var(--text)" },
        ...sx,
      }}
      {...props}
    >
      {children}
    </Box>
  );
}
